import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import { XIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { startTransition, useOptimistic } from "react";
import { Link, usePathname, useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompactSelect, Inline, SearchForm } from "@/components/workspace/people-styles";
import { MultiSelectField, SelectField } from "@/components/workspace/select-field";
import { SourceIcon } from "@/components/workspace/source-icon";
import { Toolbar } from "@/components/workspace/toolbar";
import { apiClient, unwrap } from "@/lib/api-client";
import { ACTIVITY_FILTER_KEYS, TIME_RANGE_OPTIONS } from "@/lib/filters";
import { sourceLabel } from "@/lib/source-label";
import { ActivityFilterBuilder } from "./activity-filter-builder";

export function SignalsFilters({ query }: { query: Record<string, string | undefined> }) {
  const result = useQuery({
    queryKey: ["activity-filter-options"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/activity/filters", { signal })),
    staleTime: 30_000,
    retry: false,
  });
  return (
    <ActivityFilters
      query={query}
      repositories={result.data?.resources ?? []}
      types={result.data?.types ?? []}
      optionsLoading={result.isPending}
      optionsError={!!result.error}
      onRetry={() => void result.refetch()}
    />
  );
}
export function ActivityFilters({
  query,
  repositories,
  conversation = false,
  overview = false,
  types = [],
  defaults = {},
  optionsLoading = false,
  optionsError = false,
  onRetry,
}: {
  query: Record<string, string | undefined>;
  repositories: Array<{ id: string; name: string; provider: string }>;
  conversation?: boolean;
  overview?: boolean;
  types?: string[];
  defaults?: Record<string, string>;
  optionsLoading?: boolean;
  optionsError?: boolean;
  onRetry?: () => void;
}) {
  const router = useRouter(),
    path = usePathname();
  const [filters, updateFilters] = useOptimistic(
    query,
    (current, values: Record<string, string>) => ({ ...current, ...values }),
  );
  function apply(values: Record<string, string>) {
    const next = new URLSearchParams(
      Object.entries(filters).filter((e): e is [string, string] => e[1] !== undefined),
    );
    next.delete("page");

    next.delete("personId");
    for (const [key, value] of Object.entries(values)) {
      if (value || key === "review") next.set(key, value);
      else next.delete(key);
    }
    startTransition(() => {
      updateFilters(values);
      router.push(`${path}?${next}`);
    });
  }
  const active = ACTIVITY_FILTER_KEYS.some((key) => (filters[key] || "") !== (defaults[key] || ""));
  return (
    <Toolbar>
      {!overview && (
        <SearchForm
          onSubmit={(e) => {
            e.preventDefault();
            apply({ q: String(new FormData(e.currentTarget).get("q") || "") });
          }}
        >
          <Input
            aria-label="Search activity"
            key={query.q || ""}
            name="q"
            placeholder={conversation ? "Search conversations…" : "Search people and activity…"}
            defaultValue={query.q || ""}
            type="search"
            size={InputSize.SMALL}
            label="Search activity"
            hideLabel
          />
        </SearchForm>
      )}
      {!overview ? (
        <ActivityFilterBuilder
          query={filters}
          repositories={repositories}
          types={types}
          optionsLoading={optionsLoading}
          optionsError={optionsError}
          onRetry={onRetry}
          onApply={apply}
        />
      ) : (
        <>
          <CompactSelect $width={200}>
            <MultiSelectField
              disabled={optionsLoading || optionsError}
              label="Source"
              placeholder={
                optionsLoading
                  ? "Loading sources…"
                  : optionsError
                    ? "Sources unavailable"
                    : "All sources"
              }
              value={filters.repository || ""}
              onChange={(repository) => apply({ repository })}
              options={repositories.map((r) => ({
                value: r.id,
                label: sourceLabel(r.provider, r.name),
                icon: <SourceIcon source={r.provider} size={16} />,
              }))}
              showLabel={false}
            />
          </CompactSelect>
          {optionsError && (
            <Button
              label="Retry filters"
              onClick={onRetry}
              variant={ButtonVariant.TERTIARY}
              size={ButtonSize.SMALL}
            />
          )}
          <CompactSelect $width={130}>
            <SelectField
              compact
              label="Time range"
              value={filters.days || ""}
              onChange={(days) => apply({ days })}
              options={TIME_RANGE_OPTIONS}
              hideLabel
            />
          </CompactSelect>
        </>
      )}
      {active && (
        <Button
          label="Clear"
          ariaLabel="Clear activity filters"
          icon={XIcon}
          variant={ButtonVariant.TERTIARY}
          onClick={() =>
            apply(Object.fromEntries(ACTIVITY_FILTER_KEYS.map((key) => [key, defaults[key] || ""])))
          }
          size={ButtonSize.SMALL}
          tooltip="Clear activity filters"
        />
      )}
    </Toolbar>
  );
}
export function Pagination({
  total,
  page,
  path,
  query,
}: {
  total: number;
  page: number;
  path: string;
  query: Record<string, string | undefined>;
}) {
  const href = (n: number) => {
    const params = new URLSearchParams(
      Object.entries(query).filter((e): e is [string, string] => e[1] !== undefined),
    );
    params.set("page", String(n));
    return `${path}?${params}`;
  };
  return (
    <Inline>
      {page > 1 && <Link href={href(page - 1)}>← Previous</Link>}
      {page * 40 < total && <Link href={href(page + 1)}>Next →</Link>}
    </Inline>
  );
}
